import {
  Box,
  Button,
  DateField,
  Form,
  HStack,
  SubmitButton,
  Text,
  Heading,
  TextField,
} from '@medely/ui-kit';
import React from 'react';
import { useAssignmentRequestsForWorkGapForm } from './graphql';
import { IResumeItem } from '@medely/types';
import { useWorkGapMutations, WorkGapFormValuesProps } from './mutations';
import { workGapValidationSchema } from './schema';
import { RenderModalOrPageBottomLayout } from '../RenderModalOrPageBottomLayout';

type WorkGapFormProps = {
  assignmentId?: number;
  workGap?: IResumeItem;
  isModal?: boolean;
  onSuccess: () => void;
};

export const WorkGapForm = ({
  assignmentId,
  workGap,
  onSuccess,
  isModal = false,
}: WorkGapFormProps) => {
  const { assignmentRequests } = useAssignmentRequestsForWorkGapForm(Number(assignmentId));
  const { addWorkGap, editWorkGap, isLoading } = useWorkGapMutations();

  const assignment_request_id = !!assignmentRequests?.length ? assignmentRequests[0]?.id : null;

  const handleSubmit = (values: WorkGapFormValuesProps) => {
    const action = workGap ? editWorkGap : addWorkGap;

    action(
      { values, assignment_request_id },
      {
        onSuccess: () => {
          onSuccess();
        },
      },
    );
  };

  const titleText = workGap ? 'Edit work gap' : 'Add work gap';

  const footerActions = (
    <Box px={2} mt={1} pb={1}>
      {isModal ? (
        <HStack gap={2} justifyContent="space-around">
          <Button
            fullWidth
            onClick={() => onSuccess()}
            variant="outlined"
            disabled={isLoading}
            testId="work-gap-modal-back-button"
          >
            Back
          </Button>
          <SubmitButton
            fullWidth
            disabled={isLoading}
            loading={isLoading}
            testId="work-gap-modal-save-button"
          >
            Save
          </SubmitButton>
        </HStack>
      ) : (
        <SubmitButton
          fullWidth
          disabled={isLoading}
          loading={isLoading}
          testId="work-gap-modal-save-button"
        >
          Save
        </SubmitButton>
      )}
    </Box>
  );

  const formComponent = (
    <Box flex={1} py={isModal ? 0 : 4}>
      {!isModal && (
        <Heading size="s" color="text.primary">
          {titleText}
        </Heading>
      )}
      <Box pt={isModal ? 0 : 1}>
        <Text size="m" color="text.secondary">
          Please provide information about any work gaps you may have taken.
        </Text>
      </Box>
      <Box py={isModal ? 2 : 1} />
      <HStack justifyContent="space-between">
        <Box width="48%" testId="work-gap-form-start-date">
          <DateField name="start_date" label="Start date" labelStyle="inline" />
        </Box>
        <Box width="48%" testId="work-gap-form-end-date">
          <DateField name="end_date" label="End date" labelStyle="inline" />
        </Box>
      </HStack>
      <Box py={0.5} />
      <Box testId="work-gap-form-explanation">
        <TextField
          name="explanation"
          label=""
          placeholder="Work gap explanation*"
          multiline
          rows={6}
          testId="work-gap-form-explanation"
        />
      </Box>
      <Box flex={1} />
    </Box>
  );

  return (
    <Form schema={workGapValidationSchema} handleSubmit={handleSubmit} defaultValues={workGap}>
      <RenderModalOrPageBottomLayout
        isModal={isModal}
        footerActions={footerActions}
        formComponent={formComponent}
      />
    </Form>
  );
};
